import React, { createContext, useEffect, useContext, useMemo } from 'react';
import { useProjects } from './ProjectsContext';
import usePollingStore from '../store/Polling';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface PollingContextType {
  isChecking: boolean;
  startPollingTicketsStatus: () => void;
  setCreateTicketsResponse: (response: any) => void;
  sentProjects: any[];
  setSentProjects: (projects: any[]) => void;
  createTicketsResponse: any;
  setIsChecking: (isChecking: boolean) => void;
}

const PollingContext = createContext<PollingContextType | null>(null);

export const PollingProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  
  const projectsCtx = useProjects();
  const refetchProjects = useMemo(
    () => projectsCtx?.refetchProjects ?? (() => {}),
    [projectsCtx?.refetchProjects]
  );

  const {
    isChecking,
    startPolling,
    createTicketsResponse,
    sentProjects,
    setCreateTicketsResponse,
    pollingCompleted,
    setSentProjects,
    setIsChecking,
    startPollingTicketsStatus,
    checkTicketsStatusWithBackoff,
  } = usePollingStore();

  useEffect(() => {
    if (!Array.isArray(sentProjects)) {
      setSentProjects([]);
    }
  }, [sentProjects, setSentProjects]);

  useEffect(() => {
    // Only start polling if we have a response AND polling hasn't been completed
    if (createTicketsResponse !== null && !pollingCompleted) {
      startPollingTicketsStatus();
    }
  }, [createTicketsResponse, pollingCompleted, startPollingTicketsStatus]);

  // Set up polling interval
  useEffect(() => {
    if (startPolling && isChecking) {
      const intervalId = setInterval(() => {
        checkTicketsStatusWithBackoff(refetchProjects);
      }, 5000);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [
    checkTicketsStatusWithBackoff,
    isChecking,
    startPolling,
    refetchProjects,
  ]);

  const contextValue = useMemo(
    () => ({
      isChecking,
      startPollingTicketsStatus,
      setCreateTicketsResponse,
      sentProjects,
      setSentProjects,
      createTicketsResponse,
      setIsChecking,
    }),
    [
      isChecking,
      startPollingTicketsStatus,
      setCreateTicketsResponse,
      sentProjects,
      setSentProjects,
      createTicketsResponse,
      setIsChecking,
    ]
  );

  return (
    <PollingContext.Provider value={contextValue}>
      {children}
    </PollingContext.Provider>
  );
};

export const usePolling = (): PollingContextType | null =>
  useContext(PollingContext);
